import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType } from '@udemy/browse-event-tracking';
import { Tracker } from '@udemy/event-tracking';
import { useI18n } from '@udemy/i18n';
import { range } from '@udemy/shared-utils';
import { ALL_CERTIFICATION_EXAM_VOUCHERS_URL, fetchExamVouchersAsBrowseNavItems } from '../../../api/fetch-exam-vouchers';
import { AGGREGATOR_NAV } from '../../../browse-constants';
import { useMobileNavStore } from '../../../hooks/use-mobile-nav-store';
import styles from "../mobile-nav.module.css";
import { MobileNavItem, MobileNavL1Nav, MobileNavL2Nav, MobileNavSection, MobileNavSectionHeading } from '../mobile-nav.react-component';

/** Voucher-level item for L2 (title + url). */

/** Id for the Exam Vouchers L2 drawer (categories level). */
const EXAM_VOUCHERS_L2_DRAWER_ID = 'header-toggle-side-nav-exam-vouchers';

/** Returns voucher children of a provider item as a list of rows (elements level). */
function getProviderVoucherRows(provider) {
  var _provider$children;
  const children = (_provider$children = provider.children) !== null && _provider$children !== void 0 ? _provider$children : [];
  return children.filter(c => 'url' in c && typeof c.url === 'string').map(c => ({
    id: c.id,
    title: c.title,
    url: c.url
  }));
}
export const MobileAggregatorNav = observer(({
  isSplitNavByCategoriesEnabled
}) => {
  const {
    gettext
  } = useI18n();
  const mobileNavStore = useMobileNavStore();
  const [examVoucherProviders, setExamVoucherProviders] = useState(null);
  const [examVouchersLoading, setExamVouchersLoading] = useState(false);

  // When split categories FF is on, fetch exam vouchers for the Exam Vouchers section (L1 = categories, L2 = vouchers per category).
  useEffect(() => {
    if (!isSplitNavByCategoriesEnabled) {
      setExamVoucherProviders([]);
      return;
    }
    let cancelled = false;
    setExamVouchersLoading(true);
    fetchExamVouchersAsBrowseNavItems().then(providerItems => {
      if (!cancelled) {
        setExamVoucherProviders(providerItems);
      }
    }).catch(() => {
      if (!cancelled) {
        setExamVoucherProviders([]);
      }
    }).finally(() => {
      if (!cancelled) {
        setExamVouchersLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gettext is stable; re-fetch only when FF changes
  }, [isSplitNavByCategoriesEnabled]);
  function handleAggregatorNavItemClick(item) {
    const context = {
      id: String(item.id),
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      type: item.type,
      relatedEntityId: item.id,
      relatedEntityType: item.type,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function handleExamVoucherChildClick(row) {
    const context = {
      id: row.id,
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      type: BrowseNavigationItemType.CERTIFICATION_PREPARATION,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function renderAggregatorNavItemList(items) {
    return items && items.length ? items.map(item => renderAggregatorNavItem(item)) : range(6).map(i => /*#__PURE__*/React.createElement(MobileNavItem, {
      key: `aggregator-id-${i}`,
      loading: true
    }));
  }
  function handleExamVouchersL1Click() {
    const context = {
      id: 'exam-vouchers',
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      type: BrowseNavigationItemType.CERTIFICATION_VOUCHER,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function handleAllCertificationsClick() {
    const item = AGGREGATOR_NAV(gettext).AGGREGATORS;
    Tracker.publishEvent(new BrowseNavigationItemSelected({
      id: String(item.id),
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      type: item.type,
      uiRegion: 'main_nav'
    }));
  }
  function renderAggregatorNavItem(item) {
    return /*#__PURE__*/React.createElement(MobileNavItem, {
      onClick: () => handleAggregatorNavItemClick(item),
      key: `mobile-nav-aggregator-id-${item.id}`,
      href: item.absolute_url
    }, item.title);
  }

  /** L1 Get Certified: Exam Vouchers section — single row "Exam Vouchers" that opens the Exam Vouchers (categories) drawer. */
  function renderExamVouchersSection() {
    if (!isSplitNavByCategoriesEnabled) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, examVouchersLoading ? /*#__PURE__*/React.createElement(MobileNavItem, {
      key: "exam-voucher-skeleton",
      loading: true
    }) : /*#__PURE__*/React.createElement(MobileNavItem, {
      cssToggleId: EXAM_VOUCHERS_L2_DRAWER_ID,
      onClick: handleExamVouchersL1Click
    }, gettext('Exam Vouchers')));
  }
  function renderAllCertificationsNavItem(isSubjects) {
    if (!isSplitNavByCategoriesEnabled) {
      return null;
    }
    return /*#__PURE__*/React.createElement(MobileNavItem, {
      href: AGGREGATOR_NAV(gettext).AGGREGATORS.absolute_url,
      onClick: handleAllCertificationsClick
    }, /*#__PURE__*/React.createElement("span", {
      className: styles['all-elements-link']
    }, isSubjects ? gettext('All Certification Subjects') : gettext('All Certification Issuers')));
  }

  /** L2 Exam Vouchers (categories): "All" link + one row per category (provider); each opens L3 (elements). */
  function renderExamVouchersL2Nav() {
    if (!isSplitNavByCategoriesEnabled) {
      return null;
    }
    const certificationsL1Id = 'header-toggle-side-nav-get-certified';
    return /*#__PURE__*/React.createElement(MobileNavL2Nav, {
      id: EXAM_VOUCHERS_L2_DRAWER_ID,
      l1NavId: certificationsL1Id,
      l1NavTitle: gettext('Exam Vouchers'),
      mainBackItemTitle: gettext('Main Menu')
    }, /*#__PURE__*/React.createElement(MobileNavSection, null, examVouchersLoading ? range(3).map(i => /*#__PURE__*/React.createElement(MobileNavItem, {
      key: `exam-voucher-cat-skeleton-${i}`,
      loading: true
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      key: "exam-vouchers-all",
      onClick: () => handleExamVoucherChildClick({
        id: 'exam-vouchers-all',
        title: gettext('All Certification Exam Vouchers'),
        url: ALL_CERTIFICATION_EXAM_VOUCHERS_URL
      }),
      href: ALL_CERTIFICATION_EXAM_VOUCHERS_URL
    }, /*#__PURE__*/React.createElement("span", {
      className: styles['all-elements-link']
    }, gettext('All Certification Exam Vouchers'))), (examVoucherProviders !== null && examVoucherProviders !== void 0 ? examVoucherProviders : []).map(provider => /*#__PURE__*/React.createElement(MobileNavItem, {
      key: provider.id,
      cssToggleId: `header-toggle-side-nav-${provider.id}`
    }, provider.title)))));
  }

  /** L3 Exam Vouchers Categories (one per provider): back to Exam Vouchers + list of voucher elements. */
  function renderExamVoucherProviderL3Navs() {
    if (!isSplitNavByCategoriesEnabled || !(examVoucherProviders !== null && examVoucherProviders !== void 0 && examVoucherProviders.length)) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, examVoucherProviders.map(provider => {
      const voucherRows = getProviderVoucherRows(provider);
      return /*#__PURE__*/React.createElement(MobileNavL2Nav, {
        key: provider.id,
        id: `header-toggle-side-nav-${provider.id}`,
        l1NavId: EXAM_VOUCHERS_L2_DRAWER_ID,
        l1NavTitle: gettext('Exam Vouchers')
      }, /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, provider.title), /*#__PURE__*/React.createElement(MobileNavSection, null, voucherRows.map(row => /*#__PURE__*/React.createElement(MobileNavItem, {
        onClick: () => handleExamVoucherChildClick(row),
        key: row.id,
        href: row.url
      }, row.title))));
    }));
  }
  const mobileNavId = isSplitNavByCategoriesEnabled ? 'header-toggle-side-nav-get-certified' : 'header-toggle-side-nav-certifications';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavL1Nav, {
    id: mobileNavId,
    title: isSplitNavByCategoriesEnabled ? gettext('Main Menu') : null
  }, /*#__PURE__*/React.createElement(MobileNavSection, null, renderExamVouchersSection()), /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, gettext('Popular Issuers')), /*#__PURE__*/React.createElement(MobileNavSection, null, renderAggregatorNavItemList(mobileNavStore.aggregatorIssuers), renderAllCertificationsNavItem(false)), /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, gettext('Popular Subjects')), /*#__PURE__*/React.createElement(MobileNavSection, null, renderAggregatorNavItemList(mobileNavStore.aggregatorSubjects), renderAllCertificationsNavItem(true))), renderExamVouchersL2Nav(), renderExamVoucherProviderL3Navs());
});
//# sourceMappingURL=mobile-aggregator-nav.react-component.js.map